/**
 * The small shared pieces of Save n Make.
 *
 * The provenance marker and the category chip live here because they appear in
 * five places and MUST look identical in all of them — the whole point of the
 * distinction is that she learns to read it at a glance, and a chip that means
 * "suggested" on one screen and "yours" on another would destroy that.
 */
import { useState } from 'react'
import Icon from '../Icon'
import {
  CATEGORY_KINDS, STATUS_LABEL, formatIngredient,
  type CategoryRef, type Flag, type Ingredient, type RecipeRow, type Provenance,
} from '../../lib/savenmake'

export function ProvenanceTag({ provenance, confidence }: { provenance: Provenance; confidence?: string }) {
  if (provenance === 'source' && confidence === 'certain') return null
  const label = provenance === 'source'
    ? (confidence === 'ambiguous' ? 'from post · unclear' : 'from post')
    : provenance === 'suggested' ? 'suggested' : 'yours'
  return <span className={`prov prov-${provenance}`}>{label}</span>
}

export function CategoryChips({
  categories, onKeep, onRemove,
}: {
  categories: CategoryRef[]
  /** Present only where a suggestion can be accepted. */
  onKeep?: (c: CategoryRef) => void
  onRemove?: (c: CategoryRef) => void
}) {
  if (!categories.length) return null
  return (
    <div className="chips">
      {categories.map((c) => (
        <span key={c.id} className={`chip ${c.source === 'suggested' ? 'is-suggested' : ''}`}>
          {c.name}
          {c.source === 'suggested' && onKeep && (
            <button type="button" onClick={() => onKeep(c)} title={`Keep ${c.name}`} aria-label={`Keep ${c.name}`}>
              <Icon name="check" size={13} />
            </button>
          )}
          {onRemove && (
            <button type="button" onClick={() => onRemove(c)} title={`Remove ${c.name}`} aria-label={`Remove ${c.name}`}>
              <Icon name="close" size={13} />
            </button>
          )}
        </span>
      ))}
    </div>
  )
}

/**
 * The source link.
 *
 * Prominent by design and present on every surface including the cooking view,
 * because "reopen the video" is the single thing she does most and burying it
 * would defeat the product. It opens the ORIGINAL post — nothing is copied.
 */
export function SourceLink({ url, platform, creator, subtle }: {
  url: string | null; platform?: string | null; creator?: string | null; subtle?: boolean
}) {
  if (!url) return null
  const where = platform && platform !== 'web'
    ? platform.charAt(0).toUpperCase() + platform.slice(1)
    : 'the original'
  return (
    <a className={`source-link ${subtle ? 'subtle' : ''}`} href={url} target="_blank" rel="noopener noreferrer">
      <Icon name="link" size={16} />
      Open on {where}
      {/* Not .muted: grey on the red button was unreadable. It takes the button's colour, softened. */}
      {creator && <span className="source-creator">· {creator}</span>}
    </a>
  )
}

/**
 * What the source did not say.
 *
 * Shown wherever she is about to rely on the recipe. Every line is something
 * the app refused to guess, which is why the panel is a feature and not an
 * apology.
 */
export function Attention({ flags, onResolve, title = 'Worth a look before you start' }: {
  flags: Flag[]
  onResolve?: (flag: Flag) => void
  title?: string
}) {
  const open = flags.filter((f) => f.resolvedAt === null)
  if (!open.length) return null
  return (
    <div className="attention">
      <h3><Icon name="alert" size={16} /> {title}</h3>
      <ul>
        {open.map((f) => (
          <li key={f.id}>
            {f.detail}
            {onResolve && (
              <>
                {' '}
                <button type="button" className="link" onClick={() => onResolve(f)}>sorted</button>
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

export function IngredientList({ ingredients, title = 'Ingredients' }: { ingredients: Ingredient[]; title?: string }) {
  if (!ingredients.length) return <p className="muted">No ingredients recorded yet.</p>
  return (
    <div className="cook-ingredients">
      <h4>{title}</h4>
      <ul>
        {ingredients.map((i) => (
          <li key={i.id}>
            <b>{formatIngredient(i).split(' ').slice(0, 2).join(' ')}</b>{' '}
            {formatIngredient(i).split(' ').slice(2).join(' ')}
            {i.confidence === 'missing' && <span className="prov prov-suggested"> · no amount given</span>}
            {i.confidence === 'ambiguous' && <span className="prov prov-suggested"> · a range in the post</span>}
          </li>
        ))}
      </ul>
    </div>
  )
}

export function RecipeCard({ recipe, onOpen, onFavorite }: {
  recipe: RecipeRow
  onOpen: (r: RecipeRow) => void
  onFavorite: (r: RecipeRow) => void
}) {
  const cooked = recipe.timesCooked > 0 || (recipe.priorMakes ?? 0) > 0
  return (
    <div className="recipe-card">
      <div className={recipe.sourceThumbUrl ? 'recipe-card-art' : 'recipe-card-art is-empty'}>
        {recipe.isSample && <span className="badge sample">Sample</span>}
        {!recipe.isSample && recipe.openFlags > 0 && <span className="badge">{recipe.openFlags} to check</span>}
        <button
          type="button" className="fav"
          aria-pressed={recipe.favorite}
          aria-label={recipe.favorite ? `Remove ${recipe.title} from favourites` : `Add ${recipe.title} to favourites`}
          onClick={(e) => { e.stopPropagation(); onFavorite(recipe) }}
        >
          <Icon name="heart" size={18} />
        </button>
        {recipe.sourceThumbUrl
          ? <img src={recipe.sourceThumbUrl} alt="" loading="lazy" />
          : <Icon name="bowl" size={56} className="fallback" />}
      </div>
      <button type="button" className="recipe-card-body" onClick={() => onOpen(recipe)} style={{ background: 'none', border: 'none', textAlign: 'left' }}>
        <h3>{recipe.title}</h3>
        <div className="recipe-card-meta">
          <span>{STATUS_LABEL[recipe.status]}</span>
          {recipe.totalMinutes && <span><Icon name="clock" size={12} /> {recipe.totalMinutes}m</span>}
          {recipe.servings && <span>serves {recipe.servings}</span>}
          {cooked && <span><Icon name="check" size={12} /> made {recipe.timesCooked || recipe.priorMakes}×</span>}
        </div>
        <CategoryChips categories={recipe.categories.slice(0, 3)} />
      </button>
    </div>
  )
}

export function Empty({ icon = 'bowl', title, children }: {
  icon?: 'bowl' | 'search' | 'book' | 'timer'
  title: string
  children?: React.ReactNode
}) {
  return (
    <div className="empty">
      <Icon name={icon} size={44} />
      <h3>{title}</h3>
      {children}
    </div>
  )
}

/**
 * Add a category, inventing one if it does not exist.
 *
 * Deliberately available everywhere a category can be set — import, editing and
 * the post-cooking review — because the moment she wants a new one is the moment
 * she is looking at the thing that needs it, and a trip to a settings screen is
 * how a vocabulary stays wrong.
 */
export function AddCategory({ onAdd, kinds = CATEGORY_KINDS }: {
  onAdd: (kind: string, name: string) => void | Promise<void>
  kinds?: { key: string; label: string }[]
}) {
  const [kind, setKind] = useState(kinds[0]!.key)
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)

  return (
    <form
      className="filter-row"
      onSubmit={async (e) => {
        e.preventDefault()
        if (!name.trim() || busy) return
        setBusy(true)
        try { await onAdd(kind, name.trim()); setName('') } finally { setBusy(false) }
      }}
    >
      <select value={kind} onChange={(e) => setKind(e.target.value)} style={{ maxWidth: 170 }} aria-label="Kind of category">
        {kinds.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
      </select>
      <input
        className="grow" value={name} onChange={(e) => setName(e.target.value)}
        placeholder="New category, e.g. Sunday special" aria-label="New category name"
      />
      <button type="submit" disabled={!name.trim() || busy}>
        <Icon name="plus" size={16} /> Add
      </button>
    </form>
  )
}
